import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InviteUserRequest } from '@redirector/shared';
import { apiGet, apiSend } from '../lib/api.js';

export type UserAccount = {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'viewer';
  createdAt: string;
  lastLoginAt: string | null;
  /** `invited` while a link is outstanding, `expired` once it has run out. */
  status: 'active' | 'invited' | 'expired';
};

export type Invitation = { user: UserAccount; invitePath: string };

export const userKeys = { all: ['users'] as const };

export function useUsers() {
  return useQuery({
    queryKey: userKeys.all,
    queryFn: () => apiGet<{ users: UserAccount[] }>('/api/users'),
  });
}

/**
 * Invites somebody, and returns the link.
 *
 * The link comes back once and is not stored anywhere the console can read, so
 * the caller has to show it to the operator there and then — which is why this
 * returns the response rather than only invalidating the list.
 */
export function useInviteUser() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: InviteUserRequest) => apiSend<Invitation>('POST', '/api/users', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
